/**
 * useSiteMaintenance — React hook for site maintenance mode detection.
 *
 * Sprint 137 (Founder directive 2026-09-22) — "Admin pudiera poner en modo
 * mantenimiento el sitio para evitar errores cuando haga deploy".
 *
 * This hook polls `/api/site-status` (public, unauthenticated, exempt from
 * the backend maintenance gate) to detect when the admin has flipped the
 * `FF_SITE_MAINTENANCE_MODE` flag on. When the flag is on + the current
 * user is NOT an admin, the AppShell redirects to `/maintenance` (static
 * page explaining the situation + retry button).
 *
 * Polling strategy:
 *   - On mount: immediate check (so the user sees the maintenance page
 *     immediately on first load if the flag is already on).
 *   - On window focus: re-check (so the user gets redirected if they
 *     switched tabs + came back + the admin flipped the flag in the
 *     background).
 *   - Every 60 seconds: background poll (covers the case where the user
 *     stays on a page for a long time without focusing the window).
 *   - On unmount: AbortController cancels the in-flight request.
 *
 * Admin exemption:
 *   - Reads `useAuth().user.role` — if admin/superadmin/it, the hook
 *     returns `maintenance: false` (so admins can browse normally while
 *     the site is in maintenance mode).
 *   - The backend middleware ALSO exempts admin roles (defense-in-depth)
 *     — so even if this hook fails to detect the role correctly, the
 *     backend middleware will allow admin requests through.
 *
 * Fail-closed:
 *   - If the fetch fails (network error, 5xx), `api.getSiteStatus`
 *     returns `{maintenance: false}` (site operational). The next poll
 *     cycle retries. If maintenance is actually on, the backend middleware
 *     will return 503 SITE_IN_MAINTENANCE on the user's next API call →
 *     the AppShell can ALSO detect that + redirect.
 *
 * §6 Anti-Invención: pure data-fetching hook — NO business logic. The
 *   admin toggle is at PUT /api/admin/features/site_maintenance_mode
 *   (existing endpoint). This hook only READS + redirects.
 *
 * Spec ref:
 *   - PRODUCTION-OPERATIONS-RULES §1 (destructive ops require explicit
 *     confirmation — the toggle itself lives at the admin endpoint).
 *   - GOBERNANZA-QA §8.3 (fail-closed default).
 *
 * Anti-P-041: 0 `as any` casts.
 * Anti-P-042: 0 `console.*` calls.
 * Anti-P-043: AbortController for in-flight fetch cancellation on unmount.
 */

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api-client';
import { useAuth } from '@/lib/use-auth';

// ─── Constants ─────────────────────────────────────────────────────────────

/**
 * Background poll interval (ms). 60s — covers the case where the user stays
 * on a page for a long time without focusing the window. The backend
 * middleware is the AUTHORITATIVE gate (returns 503 on the next API call),
 * so this poll is just a UX affordance to surface the maintenance page
 * proactively without waiting for a failed API call.
 */
const POLL_INTERVAL_MS = 60_000;

/**
 * Admin roles exempt from the maintenance redirect. Matches the backend
 * `ADMIN_ROLES` Set in site-maintenance.middleware.ts. If a future PR
 * adds a new admin role, BOTH sets must be updated (defense-in-depth).
 */
const ADMIN_ROLES = new Set(['admin', 'superadmin', 'it']);

// ─── Hook ───────────────────────────────────────────────────────────────────

export interface UseSiteMaintenanceResult {
  /** True when the site is in maintenance mode AND the current user is not an admin. */
  maintenance: boolean;
  /** True while the initial fetch is in flight. */
  isLoading: boolean;
}

/**
 * Polls /api/site-status + returns `maintenance: true` when the flag is on
 * + the current user is not an admin. The AppShell uses this to redirect
 * non-admin users to /maintenance.
 *
 * Usage:
 *   const { maintenance, isLoading } = useSiteMaintenance();
 *   React.useEffect(() => {
 *     if (maintenance && pathname !== '/maintenance') {
 *       router.replace('/maintenance');
 *     }
 *   }, [maintenance, pathname, router]);
 */
export function useSiteMaintenance(): UseSiteMaintenanceResult {
  const { user } = useAuth();
  const [maintenanceFlag, setMaintenanceFlag] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const abortRef = useRef<AbortController | null>(null);

  // Admin users are always exempt — return `maintenance: false` regardless
  // of the flag state. This short-circuits the polling so admins don't
  // trigger background fetches while they're operating in maintenance mode
  // (perf + cleaner logs — the backend middleware exempts them anyway,
  // so we don't need to track the flag for them).
  const isAdmin = !!(user?.role && ADMIN_ROLES.has(user.role));

  const checkStatus = useCallback(async () => {
    if (isAdmin) {
      setMaintenanceFlag(false);
      setIsLoading(false);
      return;
    }
    // Cancel any previous in-flight request (Anti-P-043).
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const data = await api.getSiteStatus(controller.signal);
      setMaintenanceFlag(data.maintenance === true);
    } catch {
      // api.getSiteStatus already returns {maintenance: false} on error,
      // but defensively catch any unexpected throw here too. Fail-closed.
      setMaintenanceFlag(false);
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin]);

  // Initial check on mount + when isAdmin changes (login/logout).
  useEffect(() => {
    void checkStatus();
    return () => {
      abortRef.current?.abort();
    };
  }, [checkStatus]);

  // Background poll every POLL_INTERVAL_MS.
  useEffect(() => {
    if (isAdmin) return; // skip poll for admins
    const interval = window.setInterval(() => {
      void checkStatus();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [checkStatus, isAdmin]);

  // Re-check on window focus (so the user gets redirected if they switched
  // tabs + came back + the admin flipped the flag in the background).
  useEffect(() => {
    if (isAdmin) return;
    const onFocus = () => {
      void checkStatus();
    };
    window.addEventListener('focus', onFocus);
    // Also re-check on visibilitychange (mobile browsers don't always fire
    // 'focus' when switching back from background — visibilitychange is
    // more reliable on mobile).
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [checkStatus, isAdmin]);

  // For admins, always return false (exempt). For non-admins, return the
  // flag state.
  return {
    maintenance: isAdmin ? false : maintenanceFlag,
    isLoading: isAdmin ? false : isLoading,
  };
}
